import React, { useState, useEffect } from 'react';
import { ticketAPI, userAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SafeIcon from '../../common/SafeIcon';
import { FiMessageSquare, FiXCircle, FiPlus, FiX, FiRefreshCw, FiCheck, FiFilter } from 'react-icons/fi';
import toast from 'react-hot-toast';

const AdminTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    topic: '',
    comment: '',
    assigneeId: ''
  });
  
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  
  const [commentModal, setCommentModal] = useState({ show: false, ticketId: null, comment: '' });
  const [submittingComment, setSubmittingComment] = useState(false);

  const [statusModal, setStatusModal] = useState({ show: false, ticketId: null, status: '' });
  const [submittingStatus, setSubmittingStatus] = useState(false);

  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [textFilter, setTextFilter] = useState('');
  const [showClosed, setShowClosed] = useState(false);
  
  const [aiModal, setAiModal] = useState({ show: false, extracting: false, tickets: [] });
  const [selectedFile, setSelectedFile] = useState(null);

  const flattenField = (val) => {
    if (!val) return '';
    if (typeof val === 'object') return Object.values(val).filter(Boolean).join(' ');
    return String(val);
  };

  const getUserLabel = (u) => {
    const realName = u.profile
      ? `${u.profile.firstName || ''} ${u.profile.lastName || ''}`.trim()
      : '';
    return realName || u.displayName || u.lineUserId;
  };

  const getUserSearchStr = (u) =>
    `${u.displayName || ''} ${u.lineUserId || ''} ${u.role || ''} ${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.toLowerCase();

  const stripThaiPrefix = (name) =>
    (name || '').replace(/^(นาย|นางสาว|นาง|น\.ส\.?|ด\.ช\.?|ด\.ญ\.?)\s*/u, '').trim();

  const compressImageForUpload = (file) =>
    new Promise((resolve) => {
      if (!file.type.startsWith('image/')) { resolve(file); return; }
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const MAX = 1920;
          const scale = Math.min(1, MAX / Math.max(img.width, img.height));
          if (scale === 1 && file.size < 3 * 1024 * 1024) { resolve(file); return; }
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.85);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleAiExtract = async () => {
    if (!selectedFile) {
      toast.error('กรุณาเลือกไฟล์');
      return;
    }

    const fileToProcess = await compressImageForUpload(selectedFile);
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64Data = reader.result;
      const mimeType = fileToProcess.type || selectedFile.type;

      setAiModal(prev => ({ ...prev, extracting: true }));
      try {
        const res = await ticketAPI.extractFromImage({ mimeType, data: base64Data });
        const extracted = (res.data || []).map(t => {
          const assigneeName = flattenField(t.assigneeName);
          const matchKey = stripThaiPrefix(assigneeName).toLowerCase();
          const match = users.find(u => matchKey && getUserSearchStr(u).includes(matchKey));
          return {
            topic: flattenField(t.topic),
            comment: flattenField(t.comment),
            assigneeName,
            assigneeId: match ? match.id : '',
            searchAssignee: match ? `${getUserLabel(match)} (${match.role})` : assigneeName,
            showDropdown: false
          };
        });

        setAiModal({ show: true, extracting: false, tickets: extracted });
        toast.success('สกัดข้อมูลสำเร็จ');
      } catch (error) {
        console.error('Error extracting data:', error);
        toast.error('ไม่สามารถสกัดข้อมูลได้');
        setAiModal(prev => ({ ...prev, extracting: false }));
      }
    };
    reader.readAsDataURL(fileToProcess);
  };

  const saveAiTicket = async (index) => {
    const t = aiModal.tickets[index];
    if (!t.topic || !t.assigneeId) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    try {
      await ticketAPI.createTicket({
        topic: t.topic,
        comment: t.comment || '',
        assigneeId: parseInt(t.assigneeId)
      });
      toast.success('สร้างรายการเรียบร้อยแล้ว');
      // Remove from list
      const newTickets = [...aiModal.tickets];
      newTickets.splice(index, 1);
      setAiModal(prev => ({ ...prev, tickets: newTickets }));
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('ไม่สามารถสร้างรายการได้');
    }
  };

  const saveAllAiTickets = async () => {
    const validTickets = aiModal.tickets.filter(t => t.topic && t.assigneeId);
    if (validTickets.length === 0) {
      toast.error('ไม่มีรายการที่ข้อมูลครบถ้วน');
      return;
    }
    
    setAiModal(prev => ({ ...prev, extracting: true }));
    let successCount = 0;
    try {
      for (const t of validTickets) {
        await ticketAPI.createTicket({
          topic: t.topic,
          comment: t.comment || '',
          assigneeId: parseInt(t.assigneeId)
        });
        successCount++;
      }
      toast.success(`สร้างรายการเรียบร้อยแล้ว ${successCount} รายการ`);
      const remaining = aiModal.tickets.filter(t => !(t.topic && t.assigneeId));
      setAiModal({ show: true, extracting: false, tickets: remaining });
      fetchTickets();
    } catch (error) {
      console.error('Error creating tickets:', error);
      toast.error('พบข้อผิดพลาดบางรายการ');
      setAiModal(prev => ({ ...prev, extracting: false }));
    }
  };

  const updateAiTicket = (index, field, value) => {
    const newTickets = [...aiModal.tickets];
    newTickets[index][field] = value;
    setAiModal(prev => ({ ...prev, tickets: newTickets }));
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const [ticketsRes, usersRes] = await Promise.all([
        ticketAPI.getTickets(),
        userAPI.getUsers({ limit: 100 })
      ]);
      setTickets(ticketsRes.data);
      if (usersRes.data && usersRes.data.items) {
        setUsers(usersRes.data.items);
      } else if (Array.isArray(usersRes.data)) {
        setUsers(usersRes.data);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('ไม่สามารถโหลดข้อมูล Tickets ได้');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.topic || !formData.assigneeId) {
      toast.error('กรุณากรอกข้อมูลให้ครบถ้วน');
      return;
    }
    
    setSubmitting(true);
    try {
      await ticketAPI.createTicket({
        ...formData,
        assigneeId: parseInt(formData.assigneeId)
      });
      toast.success('สร้างรายการเรียบร้อยแล้ว');
      setShowModal(false);
      setFormData({ topic: '', comment: '', assigneeId: '' });
      setAssigneeSearch('');
      fetchTickets();
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast.error('ไม่สามารถสร้างรายการได้');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceClose = async (id) => {
    if (!window.confirm('คุณต้องการบังคับปิดรายการนี้ใช่หรือไม่? (Force Close)')) return;
    try {
      await ticketAPI.forceClose(id);
      toast.success('บังคับปิดรายการเรียบร้อยแล้ว');
      fetchTickets();
    } catch (error) {
      console.error('Error force closing ticket:', error);
      toast.error('ไม่สามารถปิดรายการได้');
    }
  };

  const handleStatusChange = async (e) => {
    e.preventDefault();
    if (!statusModal.status) return;
    setSubmittingStatus(true);
    try {
      await ticketAPI.updateTicket(statusModal.ticketId, { status: statusModal.status });
      toast.success('เปลี่ยนสถานะเรียบร้อยแล้ว');
      setStatusModal({ show: false, ticketId: null, status: '' });
      fetchTickets();
    } catch (error) {
      console.error('Error changing status:', error);
      toast.error('ไม่สามารถเปลี่ยนสถานะได้');
    } finally {
      setSubmittingStatus(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentModal.comment) return;
    setSubmittingComment(true);
    try {
      await ticketAPI.updateTicket(commentModal.ticketId, { comment: commentModal.comment });
      toast.success('เพิ่มความเห็นแล้ว');
      setCommentModal({ show: false, ticketId: null, comment: '' });
      fetchTickets();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('ไม่สามารถเพิ่มความเห็นได้');
    } finally {
      setSubmittingComment(false);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'created': return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">สร้างใหม่</span>;
      case 'notify': return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">แจ้งเตือนแล้ว</span>;
      case 'pending': return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">รอดำเนินการ</span>;
      case 'closed': return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">ปิดแล้ว</span>;
      default: return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (!showClosed && t.status === 'closed') return false;

    if (dateFilter.from || dateFilter.to) {
      const ticketDate = new Date(t.createdAt).setHours(0, 0, 0, 0);
      const fromDate = dateFilter.from ? new Date(dateFilter.from).setHours(0, 0, 0, 0) : null;
      const toDate = dateFilter.to ? new Date(dateFilter.to).setHours(23, 59, 59, 999) : null;
      
      if (fromDate && toDate && (ticketDate < fromDate || ticketDate > toDate)) return false;
      if (fromDate && !toDate && ticketDate < fromDate) return false;
      if (!fromDate && toDate && ticketDate > toDate) return false;
    }

    if (textFilter) {
      const search = textFilter.toLowerCase();
      const textMatch = [
        t.topic,
        t.createdBy?.displayName,
        t.createdBy?.lineUserId,
        t.assignee?.displayName,
        t.assignee?.lineUserId,
        t.assignee?.role,
        ...(t.comments || []).map(c => `${c.text} ${c.userName}`)
      ].some(val => val && String(val).toLowerCase().includes(search));
      
      if (!textMatch) return false;
    }

    return true;
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pb-20 lg:pb-6">
      <div className="sm:flex sm:items-center justify-between mb-4">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">จัดการ Tickets (Admin)</h1>
          <p className="text-sm text-gray-600">ดูรายการแจ้งและจัดการสถานะ</p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none flex space-x-2">
          <button
            onClick={() => setAiModal({ show: true, extracting: false, tickets: [] })}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 sm:w-auto"
          >
            AI Extract
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            <SafeIcon icon={FiPlus} className="-ml-1 mr-2 h-5 w-5" />
            สร้างรายการใหม่
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 text-sm bg-gray-50 p-3 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiFilter} className="text-gray-400 flex-shrink-0 w-5 h-5" />
          <span className="text-gray-700 font-medium hidden lg:inline">กรองวันที่:</span>
          <div className="flex flex-1 sm:flex-none space-x-2 items-center">
            <input 
              type="date" 
              className="p-1.5 text-xs sm:text-sm border border-gray-300 rounded text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
              value={dateFilter.from}
              onChange={e => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
            />
            <span className="text-gray-400 font-medium">-</span>
            <input 
              type="date" 
              className="p-1.5 text-xs sm:text-sm border border-gray-300 rounded text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
              value={dateFilter.to}
              onChange={e => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex-1 flex items-center space-x-2">
          <input 
            type="text"
            placeholder="ค้นหาข้อความ, ชื่อ, รายละเอียด..."
            className="flex-1 p-1.5 text-xs sm:text-sm border border-gray-300 rounded text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
            value={textFilter}
            onChange={e => setTextFilter(e.target.value)}
          />
        </div>

        <div className="flex items-center">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              checked={showClosed}
              onChange={e => setShowClosed(e.target.checked)}
            />
            <span className="text-gray-700 font-medium text-xs sm:text-sm whitespace-nowrap">แสดงรายการที่ปิดแล้ว</span>
          </label>
        </div>
      </div>

      {filteredTickets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-200">
          <SafeIcon icon={FiMessageSquare} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">ไม่มีรายการ Ticket ในระบบ</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
          {filteredTickets.map((ticket) => (
            <div
              key={ticket.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-5 flex flex-col hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug break-words min-w-0">
                  {ticket.topic}
                </h3>
                <div className="flex-shrink-0">{getStatusBadge(ticket.status)}</div>
              </div>

              <div className="text-sm text-gray-600 mb-3 flex-1">
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1.5">
                  <span className="text-gray-400">ผู้สร้าง:</span>
                  <span className="text-gray-700 break-words">{ticket.createdBy?.displayName || ticket.createdBy?.lineUserId || 'Unknown'}</span>
                  <span className="text-gray-400">ผู้รับผิดชอบ:</span>
                  <span className="font-medium text-gray-800 break-words">{ticket.assignee?.displayName || ticket.assignee?.lineUserId || 'Unknown'}</span>
                  <span className="text-gray-400">วันที่:</span>
                  <span className="text-gray-700">{new Date(ticket.createdAt).toLocaleDateString('th-TH')}</span>
                </div>
                {ticket.comments && ticket.comments.length > 0 && (
                  <div className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100 max-h-44 overflow-y-auto">
                    <span className="text-gray-500 block mb-2 text-xs font-semibold uppercase tracking-wide">ประวัติรายการ</span>
                    <ul className="space-y-2">
                      {ticket.comments.map((c, idx) => (
                        <li key={idx} className="text-xs pb-2 border-b border-gray-200 last:border-0 last:pb-0">
                          <div className="flex flex-wrap items-baseline gap-x-2">
                            <span className="font-medium text-gray-800">{c.userName}</span>
                            <span className="text-gray-400">
                              {new Date(c.createdAt).toLocaleString('th-TH', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit'
                              })}
                            </span>
                          </div>
                          <p className={`mt-1 break-words ${c.type === 'status_change' ? 'text-indigo-600 font-medium' : 'text-gray-600'}`}>
                            {c.text}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {ticket.status !== 'closed' && (
                <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-100 mt-auto">
                  <button
                    onClick={() => setCommentModal({ show: true, ticketId: ticket.id, comment: '' })}
                    className="inline-flex items-center justify-center px-2 py-2 text-xs font-medium rounded-lg text-blue-700 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
                  >
                    <SafeIcon icon={FiMessageSquare} className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    คอมเมนต์
                  </button>
                  <button
                    onClick={() => setStatusModal({ show: true, ticketId: ticket.id, status: ticket.status })}
                    className="inline-flex items-center justify-center px-2 py-2 text-xs font-medium rounded-lg text-gray-700 bg-gray-50 border border-gray-200 hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
                  >
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    สถานะ
                  </button>
                  <button
                    onClick={() => handleForceClose(ticket.id)}
                    className="inline-flex items-center justify-center px-2 py-2 text-xs font-medium rounded-lg text-white bg-red-600 border border-transparent hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500"
                  >
                    <SafeIcon icon={FiXCircle} className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
                    ปิด
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {statusModal.show && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">เปลี่ยนสถานะ</h2>
              <button onClick={() => setStatusModal({ show: false, ticketId: null, status: '' })} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleStatusChange} className="p-4 space-y-4">
              <div>
                <select
                  value={statusModal.status}
                  onChange={(e) => setStatusModal(prev => ({ ...prev, status: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                  required
                >
                  <option value="created">สร้างใหม่ (Created)</option>
                  <option value="notify">แจ้งเตือน (Notify)</option>
                  <option value="pending">รอดำเนินการ (Pending)</option>
                  <option value="closed">ปิดรายการ (Closed)</option>
                </select>
              </div>
              <div className="pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setStatusModal({ show: false, ticketId: null, status: '' })}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submittingStatus}
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {submittingStatus && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {commentModal.show && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">เพิ่มคอมเมนต์</h2>
              <button onClick={() => setCommentModal({ show: false, ticketId: null, comment: '' })} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAddComment} className="p-4 space-y-4">
              <div>
                <textarea
                  value={commentModal.comment}
                  onChange={(e) => setCommentModal(prev => ({ ...prev, comment: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="พิมพ์ข้อความที่นี่..."
                  required
                ></textarea>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setCommentModal({ show: false, ticketId: null, comment: '' })}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submittingComment}
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {submittingComment && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {aiModal.show && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 shrink-0">
              <h2 className="text-lg font-medium text-gray-900">AI Extract Tickets from Image/PDF</h2>
              <button onClick={() => setAiModal({ show: false, extracting: false, tickets: [] })} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200 shrink-0 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">เลือกไฟล์รูปภาพ/เอกสาร</label>
                <input 
                  type="file" 
                  accept="image/png, image/jpeg, application/pdf"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>
              <button
                onClick={handleAiExtract}
                disabled={aiModal.extracting || !selectedFile}
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-400"
              >
                {aiModal.extracting && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                Extract Data
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {aiModal.extracting ? (
                <div className="py-10 text-center">
                  <LoadingSpinner />
                  <p className="mt-4 text-sm text-gray-500">AI กำลังวิเคราะห์ข้อมูล กรุณารอสักครู่...</p>
                </div>
              ) : aiModal.tickets.length > 0 ? (
                <div className="space-y-4">
                  {aiModal.tickets.map((t, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">หัวข้อ (Topic)</label>
                          <input
                            type="text"
                            value={t.topic || ''}
                            onChange={(e) => updateAiTicket(idx, 'topic', e.target.value)}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-2 py-1 border"
                          />
                        </div>
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-700 mb-1">ผู้รับผิดชอบ (Assignee) <span className="text-gray-400 font-normal">พบ: {t.assigneeName}</span></label>
                          <input
                            type="text"
                            value={t.searchAssignee || ''}
                            onChange={(e) => {
                              updateAiTicket(idx, 'searchAssignee', e.target.value);
                              updateAiTicket(idx, 'showDropdown', true);
                              if (t.assigneeId) updateAiTicket(idx, 'assigneeId', '');
                            }}
                            onFocus={() => updateAiTicket(idx, 'showDropdown', true)}
                            onBlur={() => setTimeout(() => updateAiTicket(idx, 'showDropdown', false), 200)}
                            className={`block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-2 py-1 border ${!t.assigneeId ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                            placeholder="ค้นหาผู้รับผิดชอบ..."
                          />
                          {t.showDropdown && (
                            <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-40 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm border border-gray-200">
                              {users.filter(u => getUserSearchStr(u).includes(stripThaiPrefix(t.searchAssignee || '').toLowerCase())).map(u => (
                                <li
                                  key={u.id}
                                  className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white text-gray-900"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    updateAiTicket(idx, 'assigneeId', u.id);
                                    updateAiTicket(idx, 'searchAssignee', `${getUserLabel(u)} (${u.role})`);
                                    updateAiTicket(idx, 'showDropdown', false);
                                  }}
                                >
                                  {getUserLabel(u)} ({u.role})
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">รายละเอียดเพิ่มเติม (Comment)</label>
                          <textarea
                            value={t.comment || ''}
                            onChange={(e) => updateAiTicket(idx, 'comment', e.target.value)}
                            rows="2"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-2 py-1 border"
                          />
                        </div>
                      </div>
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => saveAiTicket(idx)}
                          disabled={!t.topic || !t.assigneeId}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:bg-gray-300"
                        >
                          Save this
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center text-gray-500">
                  <SafeIcon icon={FiMessageSquare} className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                  <p>ยังไม่มีข้อมูลที่ถูกดึงออกมา</p>
                </div>
              )}
            </div>
            
            {aiModal.tickets.length > 0 && (
              <div className="p-4 border-t border-gray-200 shrink-0 bg-gray-50 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setAiModal({ show: false, extracting: false, tickets: [] })}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  ปิด
                </button>
                <button
                  type="button"
                  onClick={saveAllAiTickets}
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                >
                  Save All
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-xl overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">สร้างรายการใหม่</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <SafeIcon icon={FiX} className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">หัวข้อ (Topic) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  name="topic"
                  value={formData.topic}
                  onChange={handleInputChange}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                  placeholder="เช่น ขอให้ตรวจสอบระบบ..."
                />
              </div>
              
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">ผู้รับผิดชอบ (Assignee) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={assigneeSearch}
                  onChange={(e) => {
                    setAssigneeSearch(e.target.value);
                    setShowAssigneeDropdown(true);
                    if (formData.assigneeId) setFormData(prev => ({...prev, assigneeId: ''}));
                  }}
                  onFocus={() => setShowAssigneeDropdown(true)}
                  onBlur={() => setTimeout(() => setShowAssigneeDropdown(false), 200)}
                  placeholder="พิมพ์ชื่อเพื่อค้นหา..."
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                  required={!formData.assigneeId}
                />
                {showAssigneeDropdown && (
                  <ul className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm border border-gray-200">
                    {users.filter(u => getUserSearchStr(u).includes(stripThaiPrefix(assigneeSearch).toLowerCase())).map(u => (
                      <li
                        key={u.id}
                        className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white text-gray-900"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setFormData(prev => ({...prev, assigneeId: u.id}));
                          setAssigneeSearch(`${getUserLabel(u)} (${u.role})`);
                          setShowAssigneeDropdown(false);
                        }}
                      >
                        {getUserLabel(u)} ({u.role})
                      </li>
                    ))}
                    {users.filter(u => getUserSearchStr(u).includes(stripThaiPrefix(assigneeSearch).toLowerCase())).length === 0 && (
                      <li className="text-gray-500 py-2 px-3 text-sm">ไม่พบผู้ใช้งาน</li>
                    )}
                  </ul>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รายละเอียดเพิ่มเติม</label>
                <textarea
                  name="comment"
                  value={formData.comment}
                  onChange={handleInputChange}
                  rows="3"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 border"
                  placeholder="พิมพ์รายละเอียดที่นี่..."
                ></textarea>
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  ยกเลิก
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  {submitting && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2 animate-spin" />}
                  บันทึก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminTickets;
